import { hydrateCollectionsFilterUI } from '../utils/collection-filter-hydration';
import { linkDeviceSelectDuplicate } from '../modules/device-select-duplicate';
import { loadLists, loadViewState, markListRestored, restoreScroll, saveLists, saveViewState, type ListToSave } from '../utils/collection-state-store';

export {};

// Types for Storefront GraphQL product responses
interface TFImageNode {
  url: string;
  altText: string | null;
  id?: string;
}

interface TFImagesConnection {
  edges: { node: TFImageNode }[];
}

interface TFSelectedVariant {
  id: string;
  image: { url: string; altText: string | null } | null;
  availableForSale?: boolean;
}

interface TFPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface TFProduct {
  id: string;
  handle: string;
  title: string;
  availableForSale: boolean;
  vendor: string;
  productType: string;
  createdAt: string;
  tags: string[];
  metafield: { value: string } | null;
  featuredImage: TFImageNode | null;
  priceRange: TFPriceRange;
  compareAtPriceRange?: TFPriceRange | null;
  selectedOrFirstAvailableVariant?: TFSelectedVariant | null;
  variantYes?: TFSelectedVariant | null;
  variantNo?: TFSelectedVariant | null;
  wishlistVariant?: { id: string } | null;
  images?: TFImagesConnection;
}

interface TFProductsConnection {
  pageInfo: { hasNextPage: boolean; endCursor: string };
  edges: { node: TFProduct }[];
}

interface TFGraphQLResponse {
  data?: {
    collection?: {
      products?: TFProductsConnection;
    };
  };
}

// Configuration
const tfSelectors = {
  mainContent: '#MainContent',
  sections: '[data-product-section]',
  collectionHandle: '[data-collection-handle]',
  loadMoreButton: '[data-load-more]',
  filters: {
    toggleDevices: '[data-toggle-device]',
    toggleProductType: '[data-toggle-product-type]',
    devicesContainer: '.pt_filters_container',
    productTypeContainer: '.pt_filter',
    phoneDevice: '#phone-device'
  }
};

function updateSectionBatchVisibility(sectionEl: Element | null, mode: string): void {
  if (!sectionEl) return;
  const items = sectionEl.querySelectorAll('li.grid__item, li.grid__item__promo');
  items.forEach((item) => {
    const loadIndex = item.getAttribute('data-load') || '1';
    if (mode === 'first_only') {
      if (loadIndex !== '1') {
        item.classList.add('--hidden');
      } else {
        item.classList.remove('--hidden');
      }
    } else {
      item.classList.remove('--hidden');
    }
  });
}

const tfState = {
  currentMarket: (document.querySelector('body[data-country]') as HTMLElement | null)?.dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US',
  collectionHandle: (document.querySelector('[data-collection-handle]') as HTMLElement | null)?.dataset.collectionHandle || '',
  currentLanguage: document.documentElement.lang.toUpperCase(),
  shopUrl: "https://kaseme-dev-page.myshopify.com",
  accessToken: "a815422836370e8d05bd0c6a813dbaa5",
  activeColorFilter: [] as string[],
  activeDesignFilter: [] as string[],
  activeCollectionFilter: [] as string[],
  activeForHimFilter: [] as string[]
};

// Persisted collection state (see utils/collection-state-store.ts)
const LEGACY_STATE_KEY_PREFIX = 'collectionState:';

// One snapshot per list variant: the cards differ by language and market, and the device
// filter (favPhone cookie) changes which products are listed.
function getCollectionStateKey(): string {
  const favPhone = CookieManager.getCookie('favPhone') || '';
  return [tfState.collectionHandle, tfState.currentLanguage, tfState.currentMarket, favPhone].join(':');
}

// Identifies a filter checkbox across page loads
function getFilterId(input: HTMLInputElement): string {
  return `${input.name}=${input.value}`;
}

function getCheckedFilterIds(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.filters-container input[type="checkbox"]:checked')).map(getFilterId);
}

// Called after every list change (initial load, load more, filter reset) rather than on the
// product click: an IndexedDB write started while the page unloads is not guaranteed to land.
// resetSection names the list that was just rebuilt from scratch, if any.
function saveCollectionState(resetSection?: string): void {
  const lists: ListToSave[] = [];

  document.querySelectorAll(tfSelectors.sections).forEach((sectionEl) => {
    const el = sectionEl as HTMLElement;
    const ul = el.querySelector('ul') as HTMLElement | null;
    if (!ul) return;
    // A list whose first fetch hasn't landed (or failed) still holds the card-loader skeletons
    if (ul.dataset.restored !== 'true' && ul.dataset.loaded !== 'true') return;

    const section = el.dataset.section || '';
    lists.push({
      ul,
      isReset: section === resetSection,
      state: {
        section,
        cursor: el.dataset.cursor || '',
        hasNextPage: el.dataset.hasNextPage || '',
        timeReload: ul.dataset.timeReload || '1'
      }
    });
  });

  saveLists(getCollectionStateKey(), lists, getCheckedFilterIds());
}

async function restoreCollectionState(): Promise<boolean> {
  try {
    // Free the quota held by the pre-IndexedDB format
    sessionStorage.removeItem(LEGACY_STATE_KEY_PREFIX + tfState.collectionHandle);
  } catch (e) {
    // Storage blocked: nothing to clean up
  }

  const key = getCollectionStateKey();
  const snapshot = await loadLists(key);
  if (!snapshot || snapshot.lists.length === 0) return false;

  snapshot.lists.forEach((savedSection) => {
    const sectionEl = document.querySelector(`[data-section="${savedSection.section}"]`) as HTMLElement | null;
    if (!sectionEl) return;
    const ul = sectionEl.querySelector('ul') as HTMLElement | null;
    if (!ul) return;

    ul.innerHTML = savedSection.html;
    ul.dataset.timeReload = savedSection.timeReload || '1';
    ul.dataset.restored = 'true';
    markListRestored(ul, key, savedSection.chunkCount);

    if (savedSection.cursor) {
      sectionEl.dataset.cursor = savedSection.cursor;
    }
    if (savedSection.hasNextPage !== undefined && savedSection.hasNextPage !== '') {
      sectionEl.dataset.hasNextPage = savedSection.hasNextPage;
    }

    const loadingState = sectionEl.querySelector('.load-more-row');
    if (loadingState) {
      if (sectionEl.dataset.hasNextPage && sectionEl.dataset.hasNextPage !== 'false') {
        loadingState.classList.remove('--hidden');
        loadingState.querySelector('.loader')?.classList.add('--hidden');
        loadingState.querySelector('button')?.classList.remove('--hidden');
      } else {
        loadingState.classList.add('--hidden');
      }
    }
  });

  // Restored cards are fresh nodes: rebind the wishlist buttons like after any re-render
  reinitWishlistWidget();

  // The restored lists were fetched with these filters: put them back in the UI and state before
  // the managers are built, so a list that wasn't saved yet fetches with the same filters
  const filtersContainer = document.querySelector('.filters-container');
  if (filtersContainer) {
    filtersContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.checked = snapshot.filters.includes(getFilterId(input));
    });
    syncFilterStateFromCheckboxes(filtersContainer);
  }

  return true;
}

// Runs once the managers exist: re-opening the product type comes before the scroll, since
// "Load More" hides the other types and the saved position belongs to that shorter page
function restoreViewState(filterManager: FilterManager): void {
  const view = loadViewState(getCollectionStateKey());
  if (!view) return;

  const { activeSection } = view;
  if (activeSection && activeSection !== 'all' && document.querySelector(`[data-section="${activeSection}"]`)) {
    filterManager.setActiveSection(activeSection);
  }

  restoreScroll(view, tfSelectors.sections);
}

// Utilitaires
class CookieManager {
  static setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
  }

  static getCookie(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  }
}

// Détection du navigateur
function isSafari(version: number): boolean {
  const userAgent = navigator.userAgent;
  return userAgent.indexOf("Safari") !== -1 &&
         userAgent.indexOf("Chrome") === -1 &&
         parseFloat(userAgent.split("Version/")[1]) <= version;
}

// Helpers
function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function debounce<T extends (...args: never[]) => void>(fn: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(null, args), wait);
  };
}

function initializeProductDifferenceDialog(): void {
  const trigger = document.querySelector('.product-difference-toggle');
  const dialog = document.querySelector('#product-difference-dialog') as HTMLDialogElement | null;
  if (!trigger || !dialog) return;

  const closeBtn = dialog.querySelector('.product-difference__close');

  const setNoScroll = (on: boolean) => {
    document.body.classList.toggle('--no-scroll', !!on);
  };

  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      setNoScroll(true);
    }
  });

  closeBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    dialog.close();
  });

  dialog.addEventListener('cancel', () => {
    setNoScroll(false);
  });

  dialog.addEventListener('close', () => {
    setNoScroll(false);
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      dialog.close();
    }
  });
}

// Fonctions de construction des requêtes GraphQL

function buildColorFilterFragment(): string {
  if (!tfState.activeColorFilter || tfState.activeColorFilter.length === 0) return '';
  return tfState.activeColorFilter
    .map(value => `{ productMetafield: { namespace: "custom", key: "colors", value: "${value}" } }`)
    .join(',');
}

function buildDesignFilterFragment(): string {
  if (!tfState.activeDesignFilter || tfState.activeDesignFilter.length === 0) return '';
  return tfState.activeDesignFilter
    .map(value => `{ productMetafield: { namespace: "custom", key: "designs", value: "${value}" } }`)
    .join(',');
}

function buildCollectionFilterFragment(): string {
  if (!tfState.activeCollectionFilter || tfState.activeCollectionFilter.length === 0) return '';
  return tfState.activeCollectionFilter
    .map(value => `{ productMetafield: { namespace: "custom", key: "collections", value: "${value}" } }`)
    .join(',');
}

function buildForHimFilterFragment() {
  if (!tfState.activeForHimFilter || tfState.activeForHimFilter.length === 0) return '';
  return tfState.activeForHimFilter
    .map(() => `{ productMetafield: { namespace: "custom", key: "for_him", value: "true" } }`)
    .join(',');
}

function buildQuery(first: number | string = 12, cursor: string | null = null, filters: string | null = null, queryAddon = '', collectionHandle: string | null = null): string {
  return `
    query FilteredProducts @inContext(language: ${tfState.currentLanguage}, country: ${tfState.currentMarket}) {
      collection(handle: "${collectionHandle || tfState.collectionHandle}") {
        products(first: ${first}, sortKey: COLLECTION_DEFAULT${cursor ? `, after: "${cursor}"` : ''}${filters ? `, filters: ${filters}` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              title
              availableForSale
              vendor
              productType
              createdAt
              tags
              ${queryAddon}
              metafield(namespace: "custom_fields", key: "related_products") {
                value
              }
              featuredImage {
                url
                altText
                id
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `;
}

function buildtcQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Phone Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let gloss = tfState.currentLanguage == "FR" ? "Impact (Lustré)" : "Impact (Gloss)";
  let glossName = tfState.currentLanguage == "FR" ? "Type d'étuis" : "Case Type";
  let deviceName = tfState.currentLanguage == "FR" ? "Modèle" : "Device";
  const deviceValue = CookieManager.getCookie('favPhone') || 'iPhone 17';
  // MagSafe Ring option values are "With"/"Without" in the catalog, not "Yes"/"No".
  let queryAddon = `variantYes: variantBySelectedOptions(selectedOptions: [{ name:"${deviceName}", value:"${deviceValue}"},{ name:"${glossName}", value:"${gloss}"},{ name:"MagSafe Ring", value:"With"}], ignoreUnknownOptions: true) {
    id
    availableForSale
    image {
      url
      altText
    }
  }
  variantNo: variantBySelectedOptions(selectedOptions: [{ name:"${deviceName}", value:"${deviceValue}"},{ name:"${glossName}", value:"${gloss}"},{ name:"MagSafe Ring", value:"Without"}], ignoreUnknownOptions: true) {
    id
    availableForSale
    image {
      url
      altText
    }
  }
  wishlistVariant: selectedOrFirstAvailableVariant {
    id
  }`;
  return buildQuery(first, cursor, filters, queryAddon, 'impact-cases');
}

function buildcpcQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Corpo Phone Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let gloss = tfState.currentLanguage == "FR" ? "Impact (Lustré)" : "Impact (Gloss)";
  let glossName = tfState.currentLanguage == "FR" ? "Type d'étuis" : "Case Type";
  let deviceName = tfState.currentLanguage == "FR" ? "Modèle" : "Device";
  let queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"${deviceName}", value:"${CookieManager.getCookie('favPhone') || 'iPhone 17'}"},{ name:"${glossName}", value:"${gloss}"}]) {
    id
    image {
      url
      altText
    }
  }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildccQuery(first: number | string = 12, cursor: string | null = null): string {
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if(!phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Clear Case"},{variantOption: { name:"Device", value:"${phoneDeviceValue}"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  
  let bumperType = 'Clear';
  const oldClearEl = document.querySelector('#old-clear') as HTMLInputElement | null;
  const oldClearList = oldClearEl?.value || '';
  if (oldClearList.includes(`|${phoneDeviceValue}|`)) {
    bumperType = 'White';
  }
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}, { name:"Bumper Color", value:"${bumperType}"}]) {
      id
      image {
        url
        altText
      }
    }`;
  return buildQuery(first, cursor, filters, queryAddon, 'clear-case');
}

function buildacQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Airpod Cases"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildmsQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Skin"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildaQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Phone accessories"},{ productType: "Hardshell Case"},{ productType: "Charms"},{ productType: "Screen Protector"},{ productType: "Gift Card"},{ productType: "Power Bank"},{ productType: ""},{ productType: "Phone Loop"},{ productType: "Crossbody phone strap"}]`;
  const queryAddon = `wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildicQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "iPad Folio Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let queryAddon = `images (first: 3) {
    edges {
      node {
        id
        url
        altText
      }
    }
  }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildiccQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "iPad Clear Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  return buildQuery(first, cursor, filters);
}

function buildfcQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "0"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if(!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
    id
    image {
      url
      altText
    }
  }`;
  return buildQuery(first, cursor, filters, queryAddon, 'folio-case');
}

function buildfcuQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "1"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17'
  }
  if(!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17'
  }
  let queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
    id
    image {
      url
      altText
    }
  }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildkcQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "Kindle Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

function buildcpQuery(first: number | string = 12, cursor: string | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const collectionFilter = buildCollectionFilterFragment();
  const forHimFilter = buildForHimFilterFragment();
  let filters = `[{ productType: "ChargePods"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon);
}

const CARD_IMAGE_WIDTHS = [200, 250, 300, 350, 500, 600, 700];
const CARD_IMAGE_SIZES = '(min-width: 1200px) 300px, (min-width: 990px) 250px, (min-width: 750px) 350px, (min-width: 500px) 250px, 200px';

function buildResponsiveCardImg(url: string, alt: string | null | undefined): string {
  const srcset = CARD_IMAGE_WIDTHS.map((w) => `${url}&width=${w} ${w}w`).join(', ');
  return `<img src="${url}&width=300" srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}" alt="${alt}" loading="lazy">`;
}

// Picks which variant a product card should link to and show: prefers whichever
// candidate is actually in stock, checked in priority order (variantYes/"with MagSafe
// ring" first, then variantNo, then any other resolved variant), and only falls back to
// an out-of-stock candidate if none are available — so a card never sends a customer to
// a sold-out combo when an in-stock one exists for the same (locked) device.
function pickBestVariant(...candidates: (TFSelectedVariant | null | undefined)[]): TFSelectedVariant | undefined {
  const inStock = candidates.find((variant) => variant?.availableForSale);
  if (inStock) return inStock;
  return candidates.find((variant): variant is TFSelectedVariant => !!variant);
}

function buildImage(currentProduct: TFProduct): string {
  let imageurl = currentProduct.featuredImage ? currentProduct.featuredImage.url : '';
  let imagealt = currentProduct.featuredImage ? currentProduct.featuredImage.altText : '';
  const preferredVariant = pickBestVariant(currentProduct.variantYes, currentProduct.variantNo, currentProduct.selectedOrFirstAvailableVariant);
  // Prefer the in-stock variant's own image; fall through to any other resolved variant's
  // (e.g. the in-stock one doesn't have a photo yet), before giving up and keeping the
  // product's default photo.
  const variantWithImage = [preferredVariant, currentProduct.variantYes, currentProduct.variantNo, currentProduct.selectedOrFirstAvailableVariant]
    .find((variant) => variant?.image);
  if (variantWithImage?.image) {
    imageurl = variantWithImage.image.url;
    imagealt = variantWithImage.image.altText;
  }
  let hoveraddon = '';
  if (currentProduct.productType == 'Folio Case' && currentProduct.featuredImage) {
    if (currentProduct.featuredImage) {
      const hoverURL = currentProduct.featuredImage.url;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverURL)}
        </div>`;
    }
  } else if (currentProduct.productType == 'iPad Folio Case') {
    const image = currentProduct.images?.edges[2]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }

  } else if (currentProduct.productType == 'Airpod Cases') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  } else if (currentProduct.productType == 'ChargePods') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  }
  let imageSection = '';
  if (imageurl == '') {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect --missing-image">
          <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
        </div>
    </div>`;
  } else {
    imageSection = `<div class="card__media">
      <div class="media media--transparent media--hover-effect">
        ${buildResponsiveCardImg(imageurl, imagealt)}
      </div>
      ${hoveraddon}
  </div>`;
  }


  return imageSection;
}

// Fonction pour construire le HTML d'un produit
function buildProductHtml(product: TFProduct, order: number, loadIndex = '1'): string {
  const { id, title, handle, availableForSale, selectedOrFirstAvailableVariant, variantYes, variantNo, wishlistVariant, tags, vendor, productType, priceRange, compareAtPriceRange, metafield } = product;

  const isEarlyAccess = vendor === 'Early Access';

  const selectedVariant = pickBestVariant(variantYes, variantNo, selectedOrFirstAvailableVariant);
  const variantId = selectedVariant ? selectedVariant.id.replace("gid://shopify/ProductVariant/", "") : null;
  // Impact cards fetch both MagSafe Ring variants for the locked device — the product-level
  // availableForSale can stay true even when neither combo for THIS device is in stock (a
  // different device's variant is available elsewhere on the product). Use device-specific
  // availability for those cards so the badge reflects what the customer would actually get.
  const hasDeviceVariants = variantYes !== undefined || variantNo !== undefined;
  const deviceAvailable = hasDeviceVariants ? selectedVariant?.availableForSale === true : availableForSale;
  // Falls back to a dedicated aliased query field for product types whose card doesn't otherwise request a variant (Skin, Accessories, Airpod Cases, Kindle Case, ChargePods) — the wishlist-engine app requires a real variant id or the add silently fails.
  const wishlistVariantId = variantId || (wishlistVariant ? wishlistVariant.id.replace("gid://shopify/ProductVariant/", "") : null);
  let productUrl = handle;
  if(variantId) {
      productUrl = productUrl + '?variant=' + variantId;
  }

  let new_title = title;

  if (new_title.indexOf("Clear Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("Clear Case"));
  }
  if (new_title.indexOf("étui transparent") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" étui transparent"));
  }
  if (new_title.indexOf("AirPods Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("AirPods Case"));
  }
  if (new_title.indexOf(" iPad Folio") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" iPad Folio"));
  }
  if (new_title.indexOf("MacBook skin") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("MacBook skin"));
  }
  if (new_title.indexOf(" Folio Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" Folio Case"));
  }
  if (new_title.indexOf(" ChargePods") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" ChargePods"));
  }
  if (new_title.indexOf(" ChargePod") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" ChargePod"));
  }
  if (new_title.indexOf(" étui pour liseuse") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" étui pour liseuse"))
  }
  if (new_title.indexOf(" E-Reader Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" E-Reader Case"))
  }

  let preprice = tfState.currentLanguage == 'FR' ? 'À partir de' : 'From';
  if(priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
    preprice = '';
  } else if (productType.indexOf('iPad') > -1) {
    preprice = '';
  }

  const urlPrefix = tfState.currentLanguage == 'FR' ? '/fr' : '';

  let forceBadge = '';

  if(document.body.dataset.forceBadge == 'true' && document.body.dataset.forceBadgeType?.includes(productType)) {
    forceBadge = `<span class="badge badge--${document.body.dataset.forceBadgeColor}">${document.body.dataset.forceBadgeText}</span>`;
  }

  let oosBadge = '';
  let lowStockBadge = '';
  let newBadge = '';
  let colorLabel = '';
  let finalSaleBadge = '';
  const newTag = tags.filter(tag => {
    return tag == 'New';
  });
  const finalSaleTag = tags.filter(tag => {
    return tag == 'final_sale';
  });

  if(!deviceAvailable) {
    oosBadge = tfState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
  } else {
    const lowTag = tags.filter(tag => {
      return tag == 'low-stock';
    });
    if(lowTag.length > 0) {
      lowStockBadge = tfState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>':'<span class="badge">Low Stock</span>';
    }
  }

  if(newTag.length > 0) {
    newBadge = tfState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
  }

  if(finalSaleTag.length > 0) {
    finalSaleBadge = tfState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>':'<span class="badge">Final Sale</span>';
  }

  const quickAddLabel = tfState.currentLanguage == 'FR' ? 'Ajout rapide':'Quick Add';
  if(metafield && metafield.value) {
    if (tfState.currentLanguage == 'FR') {
      colorLabel = `<div class="card__label">Plus de couleurs disponibles</div>`;
    } else {
      colorLabel = `<div class="card__label">More colors available</div>`;
    }
  }

  const productID = id.replace("gid://shopify/Product/", "");
  const quickAdd = `<div class="quick-add no-js-hidden">
      <modal-opener data-modal="#QuickAdd-${productID}">
        <button id="quick-add-template--${productID}-submit"
          type="submit" name="add"
          class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
          aria-labelledby="quick-add-template--${productID} title-template--${productID}"
          data-product-url="${urlPrefix}/products/${productUrl}">
          ${quickAddLabel}
          <div class="loading__spinner hidden">
            <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
          </div>
        </button>
      </modal-opener>
      <quick-add-modal id="QuickAdd-${productID}" class="quick-add-modal">
        <div
          role="dialog"
          aria-label="${new_title}"
          aria-modal="true"
          class="quick-add-modal__content global-settings-popup"
          tabindex="-1"
        >
          <button
            id="ModalClose-${productID}"
            type="button"
            class="quick-add-modal__toggle button button--outline"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
            </svg>
          </button>
          <div id="QuickAddInfo-${productID}" class="quick-add-modal__content-info"></div>
        </div>
      </quick-add-modal>
    </div>`;
  const clearCondition = productType.indexOf('Clear Case') > -1 && productType.indexOf('iPad') == -1;

  let price: number | string = clearCondition ? 34.99 : priceRange.minVariantPrice.amount;
  if(title.indexOf("DIY Queen Clear Case") > -1 || title.indexOf("DIY Queen transparent") > -1) {
    price = 39.95;
  }
  if (productType.indexOf('ChargePods') > -1) {
    price = 79.99;
    preprice = "";
  }

  const enPriceTag = tfState.currentLanguage == 'EN' ? '$' : '';
  const frPriceTag = tfState.currentLanguage == 'FR' ? '$' : '';
  const currencyTag = tfState.currentMarket === 'CA' ? ' CAD' : ' USD';
  const priceFormatted = parseFloat(String(price)).toFixed(2);

  const compareAtAmount = parseFloat(compareAtPriceRange?.maxVariantPrice?.amount || 0);
  const maxPrice = compareAtAmount.toFixed(2);
  const showSalePricing = productType.indexOf('ChargePods') === -1 && productType.indexOf('AirGrip') === -1 && compareAtAmount > parseFloat(price);

  let contentHTML;
  if (showSalePricing) {
    contentHTML = `<div class="price price--on-sale">
                  <div class="price__container">
                    <div class="price__sale">
                      ${enPriceTag}${price}${frPriceTag}${currencyTag}
                    </div>
                    <s class="price__compare">
                      ${enPriceTag}${maxPrice}${frPriceTag}${currencyTag}
                    </s>
                  </div>
                </div>
                ${colorLabel}`;
  } else {
    contentHTML = `<div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      ${preprice} ${enPriceTag}${priceFormatted}${frPriceTag}${currencyTag}
                    </div>
                  </div>
                </div>
                ${colorLabel}`;
  }
  if(isEarlyAccess) {
    contentHTML = `<span class="card__badge badge-early-access">${tfState.currentLanguage == 'EN' ? 'Coming Soon' : 'Bientôt disponible'}</span>`;
  }

  return `
    <li class="grid__item" style="order: ${order}" data-order="${order}" data-load="${loadIndex}">
      <div class="card-wrapper product-card-wrapper underline-links-hover ${productType.indexOf('Airpod') > -1 ? '--airpods' : ''} ${productType.indexOf('iPad') > -1  ? '--ipad' : ''}">
        <a href="${urlPrefix}/products/${productUrl}">
          <span class="visually-hidden">${new_title}</span>
        </a>
        <div class="wishlist-engine" data-product_id="${productID}" data-variant_id="${wishlistVariantId}" data-full_button="false" data-css="true"></div>
        <div class="card card--standard card--media">
          <div class="card__inner">
            ${buildImage(product)}
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${new_title}
                </a>
              </h3>
              <div class="card-information">
                ${contentHTML}
              </div>
            </div>
            <!--${quickAdd}-->
          </div>
          <div class="card__badge top left">
            ${newBadge}
            ${lowStockBadge}
            ${oosBadge}
            ${forceBadge}
            ${finalSaleBadge}
          </div>
        </div>
      </div>
    </li>
  `;
}

// Gestionnaire de filtres
class FilterManager {
  ImpactManager: ProductTypeManager | null;
  FolioManager: ProductTypeManager | null;
  ClearCaseManager: ProductTypeManager | null;
  currentActiveSection: string;
  productTypeContainerEl: Element | null;
  productTypeToggleEl: Element | null;
  devicesContainerEl: Element | null;
  devicesToggleEl: Element | null;
  debouncedResetSections: () => void;

  constructor() {
    this.ImpactManager = null;
    this.FolioManager = null;
    this.ClearCaseManager = null;
    this.currentActiveSection = "all";
    this.productTypeContainerEl = document.querySelector(tfSelectors.filters.productTypeContainer);
    this.productTypeToggleEl = document.querySelector(tfSelectors.filters.toggleProductType);
    this.devicesContainerEl = document.querySelector(tfSelectors.filters.devicesContainer);
    this.devicesToggleEl = document.querySelector(tfSelectors.filters.toggleDevices);
    this.debouncedResetSections = debounce(() => this.resetSections(), 150);
    this.initializePhoneSelector();
    this.initializeEventListeners();
    if (this.productTypeContainerEl) {
      this.initializePTFilters();
    }
  }

  disconnectLazyLoaders(): void {
    const managers = [this.ImpactManager, this.FolioManager, this.ClearCaseManager].filter(Boolean) as ProductTypeManager[];
    managers.forEach((manager) => {
      manager.lazyLoader?.disconnect();
      manager.lazyLoader = null;
    });
  }

  setManagers(ImpactManager: ProductTypeManager | null, FolioManager: ProductTypeManager | null, ClearCaseManager: ProductTypeManager | null): void {
    this.ImpactManager = ImpactManager;
    this.FolioManager = FolioManager;
    this.ClearCaseManager = ClearCaseManager;
  }

  initializeEventListeners(): void {
    const { toggleDevices, toggleProductType, devicesContainer, productTypeContainer } = tfSelectors.filters;
    document.querySelector(toggleDevices)?.addEventListener('click', () =>
      this.toggleFilter(devicesContainer, toggleDevices, toggleProductType, productTypeContainer));

    document.querySelector(toggleProductType)?.addEventListener('click', () =>
      this.toggleFilter(productTypeContainer, toggleProductType, toggleDevices, devicesContainer));
  }

  toggleFilter(targetContainer: string, activeToggle: string, otherToggle: string, otherContainer: string): void {
    const container = document.querySelector(targetContainer);
    const toggle = document.querySelector(activeToggle);

    container?.classList.toggle('--open');
    toggle?.classList.toggle('--active');

    document.querySelector(otherToggle)?.classList.remove('--active');
    document.querySelector(otherContainer)?.classList.remove('--open');
  }

  initializePhoneSelector(): void {
    const phoneSelector = document.querySelector(tfSelectors.filters.phoneDevice) as HTMLSelectElement | null;
    if(phoneSelector) {
      if(phoneSelector.options.length > 0) {
        const currentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
        phoneSelector.value = currentFavPhone;
      } else {
        let el: HTMLElement | null = phoneSelector;
        for (let i = 0; i < 5 && el?.parentElement; i++) {
          el = el.parentElement;
        }
        el?.remove();
        document.querySelector('[data-toggle-device]')?.remove();
      }
    }
    const allSelect = document.querySelectorAll('.facets-container.custom select');
    for (const select of allSelect) {
      const selectEl = select as HTMLSelectElement;
      new Choices(selectEl, {shouldSort: false, shouldSortItems: false, placeholder: selectEl.previousElementSibling?.innerHTML || ''});
      this.setupSelectorEvents(selectEl);
    }
  }

  setupSelectorEvents(selector: HTMLSelectElement): void {
    const isPhoneSelector = selector.matches(tfSelectors.filters.phoneDevice);

    if (isPhoneSelector) {
      selector.addEventListener('change', () => {
        CookieManager.setCookie('favPhone', selector.value);
        if (this.ImpactManager?.isValid && !this.ImpactManager?.isLoading) this.ImpactManager.loadMoreContent(true);
        if (this.ClearCaseManager?.isValid && !this.ClearCaseManager?.isLoading) this.ClearCaseManager.loadMoreContent(true);
        if (this.FolioManager?.isValid && !this.FolioManager?.isLoading) this.FolioManager.loadMoreContent(true);
      });
    } else {
      selector.addEventListener('change', () => {
        let shouldReload = false;
        if (selector.id && (selector.id.startsWith('color') || selector.id.startsWith('couleur'))) {
          const selectedOptions = Array.from(selector.selectedOptions || []);
          const values = selectedOptions
            .map(option => option.value)
            .filter(value => value && value.trim() !== '');
          const next = values.length > 0 ? values : [];
          if (!arraysEqual(next, tfState.activeColorFilter)) {
            tfState.activeColorFilter = next;
            shouldReload = true;
          }
        } else if (selector.id && selector.id.startsWith('design')) {
          const selectedOptions = Array.from(selector.selectedOptions || []);
          const values = selectedOptions
            .map(option => option.value)
            .filter(value => value && value.trim() !== '');
          const next = values.length > 0 ? values : [];
          if (!arraysEqual(next, tfState.activeDesignFilter)) {
            tfState.activeDesignFilter = next;
            shouldReload = true;
          }
        }
        if (shouldReload) {
          this.debouncedResetSections();
        }
      });
    }
  }

  initializePTFilters(): void {
    const productTypeSelector = document.querySelector(tfSelectors.filters.productTypeContainer);
    if (!productTypeSelector) return;

    productTypeSelector.querySelectorAll('[data-target]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();

        const nextTarget = (el as HTMLElement).dataset.target;

        if (!nextTarget || nextTarget === 'all') {
          this.setActiveSection('all');
          return;
        }

        if (this.currentActiveSection === nextTarget) {
          this.setActiveSection('all');
          return;
        }
        this.setActiveSection(nextTarget);
      });
    });
  }

  showProductTypeFilters = (): void => {
    if(this.productTypeContainerEl && this.productTypeContainerEl.querySelectorAll('[data-target]:not(.--hidden)').length > 2) {
      this.productTypeContainerEl.classList.remove('--hidden');
      this.productTypeToggleEl?.classList.remove('--hidden');
    }
  };

  hideProductTypeFilters = (): void => {
    const container = document.querySelector(tfSelectors.filters.productTypeContainer);
    const toggle = document.querySelector(tfSelectors.filters.toggleProductType);
    if (container) container.classList.add('--hidden');
    if (toggle) toggle.classList.add('--hidden');
  };

  showProductTypeFilter = (currentType: string): void => {
    const targetEl = document.querySelector(`[data-target="${currentType}"]`);
    if (targetEl && targetEl.parentElement) {
      targetEl.parentElement.removeAttribute('hidden');
    }
    this.showProductTypeFilters();
  };

  hideProductTypeFilter = (currentType: string): void => {
    const targetEl = document.querySelector(`[data-target="${currentType}"]`);
    if (targetEl && targetEl.parentElement) {
      targetEl.parentElement.setAttribute('hidden', 'hidden');
    }
  };

  setActiveManager(targetType: string): void {
    switch (targetType) {
      case 'tc':
        if (this.ImpactManager && this.ImpactManager.isValid && this.ImpactManager.section.dataset.hasNextPage != "false") this.ImpactManager.lazyLoader = new LazyLoader(this.ImpactManager);
        break;
      case 'cc':
        if (this.ClearCaseManager && this.ClearCaseManager.isValid && this.ClearCaseManager.section.dataset.hasNextPage != "false") this.ClearCaseManager.lazyLoader = new LazyLoader(this.ClearCaseManager);
        break;
      case 'fc':
        if (this.FolioManager && this.FolioManager.isValid && this.FolioManager.section.dataset.hasNextPage != "false") this.FolioManager.lazyLoader = new LazyLoader(this.FolioManager);
        break;
      default:
        break;
    }
  }

  setActiveSection(sectionAbbv: string): void {
    if(this.currentActiveSection == sectionAbbv) return;

    if(sectionAbbv == 'all') {
      this.disconnectLazyLoaders();
      document.querySelectorAll('[data-section]').forEach(element => {
        element.classList.remove('--section-hidden');
        updateSectionBatchVisibility(element, 'first_only');
      });
    } else {
      document.querySelectorAll('[data-section]').forEach(element => {
        if((element as HTMLElement).dataset.section !== sectionAbbv) {
          element.classList.add('--section-hidden');
        } else {
          element.classList.remove('--section-hidden');
          updateSectionBatchVisibility(element, 'all');
        }
      });
    }

    const productTypeSelector = document.querySelector(tfSelectors.filters.productTypeContainer);
    if (productTypeSelector) {
      const activeBtn = productTypeSelector.querySelector('[data-target].active');
      const nextActive = productTypeSelector.querySelector('[data-target="'+ sectionAbbv +'"]');
      if (activeBtn) activeBtn.classList.remove('active');
      if (nextActive) nextActive.classList.add('active');
    }
    this.currentActiveSection = sectionAbbv;
  }

  resetSections(): void {
    const activeSectionBeforeReset = this.currentActiveSection || 'all';

    document.querySelectorAll(tfSelectors.sections).forEach(element => {
      const el = element as HTMLElement;
      el.dataset.cursor = '';
      el.dataset.hasNextPage = '';
      const currentSection = el.dataset.section;
      switch (currentSection) {
        case 'tc':
          if (this.ImpactManager && this.ImpactManager.isValid) this.ImpactManager.loadMoreContent(true);
          break;
        case 'cc':
          if (this.ClearCaseManager && this.ClearCaseManager.isValid) this.ClearCaseManager.loadMoreContent(true);
          break;
        case 'fc':
          if (this.FolioManager && this.FolioManager.isValid) this.FolioManager.loadMoreContent(true);
          break;
      }
    });

    this.currentActiveSection = '';
    this.setActiveSection(activeSectionBeforeReset);
  }
}

// Integration for the new filters UI
// Reads the checked filter inputs into tfState without reloading the lists
function syncFilterStateFromCheckboxes(filtersContainer: Element): void {
  const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
  const colorValues: string[] = [];
  const designValues: string[] = [];
  const collectionValues: string[] = [];
  const forHimValues: string[] = [];

  checkedInputs.forEach((input) => {
    const name = input.name || '';
    const key = name.split('.').pop() || '';
    const value = input.value;
    if (!value) return;

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('color') || lowerKey.includes('couleur')) {
      colorValues.push(value);
    } else if (lowerKey.includes('design') || lowerKey.includes('motif')) {
      designValues.push(value);
    } else if (lowerKey.includes('collections')) {
      collectionValues.push(value);
    } else if (lowerKey.includes('for_him') || lowerKey.includes('for-him') || lowerKey.includes('pour_lui') || lowerKey.includes('pour-lui')) {
      forHimValues.push(value);
    }
  });

  tfState.activeColorFilter = colorValues;
  tfState.activeDesignFilter = designValues;
  tfState.activeCollectionFilter = collectionValues;
  tfState.activeForHimFilter = forHimValues;
}

function initializeNewFiltersIntegration(filterManager: FilterManager, wasRestored: boolean): void {
  const filtersContainer = document.querySelector('.filters-container');
  if (!filtersContainer) return;

  const updateFilterSummaryLabels = (): void => {
    const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
    const selectionsByFilter = new Map<Element, string[]>();

    checkedInputs.forEach((input) => {
      const filterEl = input.closest('.js-filter');
      if (!filterEl) return;

      const labelEl = input.closest('label');
      const valueLabel = labelEl ? labelEl.textContent!.trim() : (input.value || '');
      if (!valueLabel) return;

      if (!selectionsByFilter.has(filterEl)) {
        selectionsByFilter.set(filterEl, []);
      }
      selectionsByFilter.get(filterEl)!.push(valueLabel);
    });

    const resetButton = filtersContainer.querySelector('[data-reset-filters]');

    filtersContainer.querySelectorAll('.js-filter').forEach((filterEl) => {
      if (filterEl.querySelector('#phone-device')) {
        return;
      }

      const activeSpan = filterEl.querySelector('.filter__active');
      if (!activeSpan) return;

      const values = selectionsByFilter.get(filterEl) || [];
      if (values.length === 0) {
        activeSpan.textContent = '';
      } else if (values.length === 1) {
        activeSpan.textContent = values[0];
      } else {
        activeSpan.textContent = `(${values.length})`;
      }
    });

    if (resetButton) {
      const hasActiveNonDeviceFilters = checkedInputs.length > 0;
      resetButton.classList.toggle('--hidden', !hasActiveNonDeviceFilters);
    }
  };

  const updateStateFromNewFilters = (): void => {
    const filtersLoader = filtersContainer.querySelector('.filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.add('--open');
    }


    syncFilterStateFromCheckboxes(filtersContainer);

    filterManager.debouncedResetSections();
    updateFilterSummaryLabels();
  };

  const newPhoneSelector = filtersContainer.querySelector('#phone-device') as HTMLSelectElement | null;
  if (newPhoneSelector) {
    const currentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
    newPhoneSelector.value = currentFavPhone;

    let drawerChoices: ChoicesInstance | null = null;
    try {
      drawerChoices = new Choices(newPhoneSelector, {shouldSort: false, shouldSortItems: false, placeholder: newPhoneSelector.previousElementSibling?.innerHTML || ''});
    } catch (e) {
      console.warn('Choices initialization failed for new phone selector', e);
    }

    // Optional copy of the device select next to the filter button
    const deviceDuplicate = linkDeviceSelectDuplicate(newPhoneSelector, drawerChoices);

    const deviceFilter = newPhoneSelector.closest('.js-filter');
    if (deviceFilter) {
      const activeSpan = deviceFilter.querySelector('.filter__active');
      if (activeSpan) {
        const selectedOption = newPhoneSelector.options[newPhoneSelector.selectedIndex];
        const label = selectedOption ? selectedOption.textContent!.trim() : '';
        activeSpan.textContent = label;
      }
    }

    newPhoneSelector.addEventListener('change', () => {
      const filtersLoader = filtersContainer.querySelector('.filters__loader');
      if (filtersLoader) {
        filtersLoader.classList.add('--open');
      }

      CookieManager.setCookie('favPhone', newPhoneSelector.value);
      deviceDuplicate?.syncFromDrawer();
      if (filterManager.ImpactManager?.isValid && !filterManager.ImpactManager?.isLoading) filterManager.ImpactManager.loadMoreContent(true);
      if (filterManager.ClearCaseManager?.isValid && !filterManager.ClearCaseManager?.isLoading) filterManager.ClearCaseManager.loadMoreContent(true);
      if (filterManager.FolioManager?.isValid && !filterManager.FolioManager?.isLoading) filterManager.FolioManager.loadMoreContent(true);

      const deviceFilter = newPhoneSelector.closest('.js-filter');
      if (deviceFilter) {
        const activeSpan = deviceFilter.querySelector('.filter__active');
        if (activeSpan) {
          const selectedOption = newPhoneSelector.options[newPhoneSelector.selectedIndex];
          const label = selectedOption ? selectedOption.textContent!.trim() : '';
          activeSpan.textContent = label;
        }
      }
    });
  }

  filtersContainer.addEventListener('change', (event) => {
    const target = event.target as HTMLElement;
    if (!target || !target.matches('input[type="checkbox"]')) return;
    updateStateFromNewFilters();
  });

  const resetButton = filtersContainer.querySelector('[data-reset-filters]');
  if (resetButton) {
    resetButton.addEventListener('click', () => {
      const filtersLoader = filtersContainer.querySelector('.filters__loader');
      if (filtersLoader) {
        filtersLoader.classList.add('--open');
      }

      const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
      checkedInputs.forEach((input) => {
        input.checked = false;
      });

      tfState.activeColorFilter = [];
      tfState.activeDesignFilter = [];
      tfState.activeCollectionFilter = [];
      tfState.activeForHimFilter = [];

      updateFilterSummaryLabels();
      filterManager.debouncedResetSections();
    });
  }

  updateFilterSummaryLabels();

  if (wasRestored) {
    // restoreCollectionState already put back the filters the lists were fetched with. The URL
    // auto-select below would reset the lists under the restored scroll position.
    updateFilterSummaryLabels();
    return;
  }

  // Auto-select filters from URL query params (?color=Red&design=Floral)
  const urlParams = new URLSearchParams(window.location.search);
  const colorParam = urlParams.get('color');
  const designParam = urlParams.get('design');
  const forHimParam = urlParams.get('for_him');
  let needsUpdate = false;

  if (colorParam) {
    const colorCheckbox = (filtersContainer.querySelector(`input[type="checkbox"][name="filter.p.m.custom.colors"][value="${colorParam}"]`)
      || filtersContainer.querySelector(`input[type="checkbox"][name="filter.p.m.custom.couleurs"][value="${colorParam}"]`)) as HTMLInputElement | null;
    if (colorCheckbox && !colorCheckbox.checked) {
      colorCheckbox.checked = true;
      needsUpdate = true;
    }
  }

  if (designParam) {
    const designCheckbox = filtersContainer.querySelector(`input[type="checkbox"][value="${designParam}"]`) as HTMLInputElement | null;
    if (designCheckbox && !designCheckbox.checked) {
      const name = (designCheckbox.name || '').toLowerCase();
      if (name.includes('design') || name.includes('motif')) {
        designCheckbox.checked = true;
        needsUpdate = true;
      }
    }
  }

  if (forHimParam) {
    const forHimCheckbox = (filtersContainer.querySelector(`input[type="checkbox"][name="filter.p.m.custom.for-him"]`)
      || filtersContainer.querySelector(`input[type="checkbox"][name="filter.p.m.custom.pour-lui"]`)) as HTMLInputElement | null;
    if (forHimCheckbox && !forHimCheckbox.checked) {
      forHimCheckbox.checked = true;
      needsUpdate = true;
    }
  }

  if (needsUpdate) {
    updateFilterSummaryLabels();
  }
}

function initializeCollectionsFilterHydration(): void {
  const filtersContainer = document.querySelector('.filters-container');
  if (!filtersContainer) return;

  hydrateCollectionsFilterUI(filtersContainer);

  const debouncedHydrate = debounce(() => hydrateCollectionsFilterUI(filtersContainer), 100);
  const observer = new MutationObserver(() => debouncedHydrate());
  observer.observe(filtersContainer, { childList: true, subtree: true });
}

// Fonction de fetch principale
// The wishlist-engine app only binds its click handlers to .wishlist-engine elements present
// at the time its bootstrap script runs — it never observes the DOM for later additions, and
// that bootstrap script removes itself from the DOM once done. window.WishEngineinit is the
// app's own re-hydration entry point; call it after every grid update so newly injected cards
// get bound too.
function reinitWishlistWidget(): void {
  const init = (window as unknown as { WishEngineinit?: () => void }).WishEngineinit;
  if (typeof init !== 'function') return;
  try {
    init();
  } catch (e) {
    console.warn('WishEngineinit failed', e);
  }
}

// Grid cells a promo takes up: its width in columns (2-col grid below 990px, 4-col above)
function promoCells(size: string): number {
  const desktop = window.matchMedia('(min-width: 990px)').matches;
  if (size === 'one') return 1;
  if (size === 'two') return desktop ? 2 : 1;
  return desktop ? 4 : 2;
}

// Products to request so the batch (products + the promos it receives) fills exactly
// nb_products cells, which keeps the last row complete on every page
function getBatchSize(section: HTMLElement, startOrder: number): number {
  const ul = section.querySelector('ul') as HTMLElement | null;
  const total = Number(ul?.dataset.pageSize);
  if (!ul || !total) return Number(ul?.dataset.ogn || 12);
  const repeat = ul.dataset.repeatPromosPerPage === 'true';
  const start = repeat ? 0 : startOrder;
  const promos = Array.from(section.querySelectorAll<HTMLTemplateElement>('template[data-position]'))
    .map(t => ({ position: Number(t.dataset.position), cells: promoCells(t.dataset.size ?? '') }));
  const cellsFor = (size: number): number => promos
    .filter(p => p.position > start && p.position <= start + size)
    .reduce((sum, p) => sum + p.cells, 0);
  let best = 1;
  for (let size = total; size >= 1; size--) {
    const filled = size + cellsFor(size);
    if (filled === total) return size;
    if (filled < total && size > best) best = size;
  }
  return best;
}

async function fetchCollectionProductsByProductType(Manager: ProductTypeManager, isReset = true, cursor: string | null = null): Promise<void | false> {
  const currentBuild = Manager.sectionType;
  try {
    let query: string | undefined;
    const batchSection = document.querySelector(`[data-section="${currentBuild}"]`) as HTMLElement | null;
    const batchStart = isReset ? 0 : parseInt((batchSection?.querySelector('ul li:last-child') as HTMLElement | null)?.dataset.order ?? '') || 0;
    const nb_products = batchSection ? String(getBatchSize(batchSection, batchStart)) : '12';
    switch (currentBuild) {
      case 'tc':
        query = buildtcQuery(nb_products, cursor);
        break;
      case 'fc':
        query = buildfcQuery(nb_products, cursor);
        break;
      case 'cc':
        query = buildccQuery(nb_products, cursor);
        break;
    }

    const section = document.querySelector(`[data-section="${currentBuild}"]`) as HTMLElement | null;
    if (!section) {
      console.warn(`Section ${currentBuild} not found`);
      return;
    }

    const loadingState = section.querySelector('.load-more-row');
    loadingState?.querySelector('.loader')?.classList.remove('--hidden');

    const response = await fetch(tfState.shopUrl + '/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': tfState.accessToken
      },
      body: JSON.stringify({
        query: query
      })
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: TFGraphQLResponse = await response.json();
    const products = data.data?.collection?.products;

    if(products) {
      if(products.edges.length > 0) {
        let currentOrder = 0;
        const lastChild = section.querySelector('ul li:last-child') as HTMLElement | null;
        if (lastChild?.dataset.order) {
          currentOrder = parseInt(lastChild.dataset.order) || 0;
        }
        if (isReset) {
          const ul = section.querySelector('ul');
          if (ul) ul.innerHTML = '';
          currentOrder = 0;
        }

        const ulElement = section.querySelector('ul') as HTMLElement | null;
        const loadIndex = ulElement?.dataset?.timeReload || '1';

        let productHTML = '';
        const repeatPromosPerPage = ulElement && ulElement.dataset.repeatPromosPerPage === 'true';

        products.edges.forEach(({ node: product }, i) => {
          const order = currentOrder + i + 1;

          const promoPosition = repeatPromosPerPage ? (i + 1) : order;

          const promoToPlace = document.querySelectorAll(
            `[data-section="${currentBuild}"] template[data-position="${promoPosition}"]`
          );

          if (promoToPlace.length > 0) {
            promoToPlace.forEach((promo) => {
              let promoHtml = promo.innerHTML;

              if (loadIndex !== '1') {
                promoHtml = promoHtml
                  .replace(/<li\s+class="([^"]+)"/g, '<li class="$1 --hidden"')
                  .replace(/data-order="(\d+)"/g, `data-order="$1" data-load="${loadIndex}"`);
              } else {
                promoHtml = promoHtml.replace(/data-order="(\d+)"/g, `data-order="$1" data-load="${loadIndex}"`);
              }

              if (repeatPromosPerPage) {
                const orderValue = order;
                promoHtml = promoHtml
                  .replace(/style="order: [^;]+;/, `style="order: ${orderValue};`)
                  .replace(/data-order="[^"]+"/, `data-order="${orderValue}"`);
              }

              productHTML += promoHtml;
            });
          }

          productHTML += buildProductHtml(product, order, loadIndex);
        });

        const ulEl = section.querySelector('ul');
        if (ulEl) ulEl.innerHTML += productHTML;
        reinitWishlistWidget();

        if (Manager.filterManager?.currentActiveSection === 'all') {
          updateSectionBatchVisibility(section, 'first_only');
        }

        section.removeAttribute('hidden');
        // FIX: original assigned boolean to dataset (must be string)
        if (products.pageInfo.hasNextPage) {
          section.dataset.cursor = products.pageInfo.endCursor;
          section.dataset.hasNextPage = 'true';
          loadingState?.classList.remove('--hidden');
          loadingState?.querySelector('.loader')?.classList.add('--hidden');
          loadingState?.querySelector('button')?.classList.remove('--hidden');
        } else {
          section.dataset.hasNextPage = 'false';
          loadingState?.classList.add('--hidden');
        }

        const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
        if (typeButton && typeButton.parentElement) {
          typeButton.parentElement.removeAttribute('hidden');
        }
      } else {
        if (Manager.sectionType != "fcu") {
          const currentList = section.querySelector('ul');
          if (currentList) {
            if (document.documentElement.lang.toUpperCase() == 'FR') {
              currentList.innerHTML = "<li class='empty'>Aucun produit ne correspond aux filtres sélectionnés</li>";
            } else {
              currentList.innerHTML = "<li class='empty'>No product matches the selected filters</li>";
            }
          }
        } else {
          section.remove();
          const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
          if (typeButton && typeButton.parentElement) {
            typeButton.parentElement.setAttribute('hidden', 'hidden');
          }
          Manager.isValid = false;
        }

        if (loadingState) loadingState.classList.add('--hidden');
      }
    } else {
      if (loadingState) loadingState.classList.add('--hidden');

      section.remove();
      const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
      if (typeButton && typeButton.parentElement) {
        typeButton.parentElement.setAttribute('hidden', 'hidden');
      }
      Manager.isValid = false;
    }
    const filtersLoader = document.querySelector('.filters-container .filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.remove('--open');
    }
    return;
  } catch (error) {
    console.error('Error fetching products:', error);
    const loadingState = document.querySelector(`[data-section="${currentBuild}"] .load-more-row`);
    if (loadingState) loadingState.classList.add('--hidden');
    const filtersLoader = document.querySelector('.filters-container .filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.remove('--open');
    }
    return false;
  }
}

// Gestionnaire de produits
class ProductTypeManager {
  section: HTMLElement;
  filterManager!: FilterManager;
  lazyLoader!: LazyLoader | null;
  isValid!: boolean;
  sectionType!: string;
  isLoading!: boolean;

  constructor(sectionType: string, filterManager: FilterManager) {
    this.section = document.querySelector(`[data-section="${sectionType}"]`) as HTMLElement;
    if (!this.section) return;
    this.filterManager = filterManager;
    this.lazyLoader = null;
    this.isValid = true;
    this.sectionType = sectionType;

    const ul = this.section.querySelector('ul') as HTMLElement | null;
    const wasRestored = ul && ul.dataset.restored === 'true';

    const setupAfterLoad = (): void => {
      if (this.isValid) {
        this.filterManager.showProductTypeFilter(this.sectionType);
        const loadMoreButton = this.section.querySelector(tfSelectors.loadMoreButton);
        if (this.section.dataset.hasNextPage === 'true') {
          loadMoreButton?.addEventListener('click', e => {
            e.preventDefault();
            this.filterManager.setActiveSection(this.sectionType);
            this.lazyLoader = new LazyLoader(this);
          });
        }
      }
      this.isLoading = false;
    };

    if (wasRestored) {
      setupAfterLoad();
    } else {
      fetchCollectionProductsByProductType(
        this, true
      ).then((result) => {
        if (result !== false) this.markLoaded();
        setupAfterLoad();
        saveCollectionState(this.sectionType);
      });
    }
  }

  // Lets saveCollectionState persist this list: until a fetch succeeds it only holds skeletons
  private markLoaded(): void {
    const ul = this.section.querySelector('ul') as HTMLElement | null;
    if (ul) ul.dataset.loaded = 'true';
  }

  loadMoreContent(isReset = false): void {
    if (!isReset && this.filterManager?.currentActiveSection === 'all') return;
    if (((this.section.dataset.hasNextPage !== 'true') && !isReset) || this.isLoading) return;
    this.isLoading = true;
    const cursor = isReset ? null : this.section.dataset.cursor || null;

    const ul = this.section.querySelector('ul') as HTMLElement | null;
    if (ul) {
      if (isReset) {
        ul.dataset.timeReload = '1';
      } else {
        ul.dataset.timeReload = String(parseInt(ul.dataset.timeReload || '1') + 1);
      }
    }

    fetchCollectionProductsByProductType(
      this,
      isReset,
      cursor
    ).then((result) => {
      if (result !== false) this.markLoaded();
      if (this.section.dataset.hasNextPage !== 'true') {
        this.lazyLoader?.disconnect();
        this.lazyLoader = null;
      }
      this.isLoading = false;
      saveCollectionState(isReset ? this.sectionType : undefined);
    });
  }

}

// Chargement paresseux
class LazyLoader {
  activeManager: ProductTypeManager;
  observer: IntersectionObserver;

  constructor(manager: ProductTypeManager) {
    this.activeManager = manager;
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      { rootMargin: '0px 0px 0px 0px', threshold: 0.1 }
    );

    const existingSentinel = manager.section.querySelector('.load-more-row') || manager.section.querySelector('[data-lazy-sentinel]');
    if (existingSentinel) {
      this.observe(existingSentinel);
    } else {
      const ul = manager.section.querySelector('ul');
      if (ul) {
        const sentinel = document.createElement('div');
        sentinel.setAttribute('data-lazy-sentinel', '');
        ul.insertAdjacentElement('afterend', sentinel);
        this.observe(sentinel);
      }
    }
  }

  observe(element: Element): void {
    if (element) {
      this.observer.observe(element);
    }
  }

  disconnect(): void {
    this.observer?.disconnect();
  }

  handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (this.activeManager.filterManager?.currentActiveSection === 'all') {
        this.disconnect();
        this.activeManager.lazyLoader = null;
        return;
      }
      if (this.activeManager.section?.dataset?.hasNextPage !== 'true') {
        this.disconnect();
        return;
      }
      if (!this.activeManager.isLoading) this.activeManager.loadMoreContent();
    });
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', async () => {
  if (isSafari(15.6)) {
    document.querySelector(tfSelectors.mainContent)?.classList.add('--safari');
  }

  initializeProductDifferenceDialog();

  // Restore (awaited) before the managers are built so they see ul[data-restored] and skip
  // their initial fetch. Restoring on pageshow was too late: the managers' first-page fetch
  // could resolve after it and wipe the restored list, dropping the user into the footer.
  // bfcache restores (pageshow.persisted) keep the live DOM, so they need nothing.
  const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const wasRestored = navEntries[0]?.type === 'back_forward' && await restoreCollectionState();

  // Pre-set filter state from URL params so initial fetches already include filters.
  // Restored lists bring their own filters (applied in restoreCollectionState).
  if (!wasRestored) {
    const _urlParams = new URLSearchParams(window.location.search);
    if (_urlParams.get('color')) tfState.activeColorFilter = [_urlParams.get('color')!];
    if (_urlParams.get('design')) tfState.activeDesignFilter = [_urlParams.get('design')!];
    if (_urlParams.get('for_him')) tfState.activeForHimFilter = ['true'];
  }

  const filterManager = new FilterManager();
  const ImpactManager = document.querySelector('[data-section="tc"]') ? new ProductTypeManager('tc', filterManager) : null;
  const FolioManager = document.querySelector('[data-section="fc"]') ? new ProductTypeManager('fc', filterManager) : null;
  const ClearCaseManager = document.querySelector('[data-section="cc"]') ? new ProductTypeManager('cc', filterManager) : null;

  filterManager.setManagers(ImpactManager, FolioManager, ClearCaseManager);

  initializeNewFiltersIntegration(filterManager, wasRestored);
  if (wasRestored) restoreViewState(filterManager);

  // pagehide fires on every way out (product card, menu, swipe back) and allows a synchronous write
  window.addEventListener('pagehide', () => saveViewState(getCollectionStateKey(), filterManager.currentActiveSection, tfSelectors.sections));

  initializeCollectionsFilterHydration();
});